import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NgbDateAdapter, NgbDateStruct } from '@cmps/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { addDays, startOfDay } from 'date-fns';
import { forkJoin } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';

import { GoalThreshold } from '../../../coaching-base/config';
import {
    CoachableMetric,
    CoachingType,
    CommitmentKpi,
} from '../../../coaching-base/general';
import { GoalService } from '../../../coaching-commitment/goal/goal.service';
import { GraphType } from '../../../coaching-insight/oa/models/oa.model';
import { TeamOAService } from '../../../coaching-insight/oa/services/team-oa.service';
import { CmpsForm, CmpsFormBuilder } from '../../../common/experimental.form';
import { takeUntilDestroy } from '../../../common/take-until-destroy.decorator';
import {
    CriteriaIdType,
    PerformanceDataViewType,
} from '../../../insight/performance-data/metric.pd.model';
import { PeriodType } from '../../../widget/date-range.widget.model';
import { DialogLayoutService } from '../dialog-layout.service';
import { TranslationKeyMap } from '../focus-selector.i18n';
import { Row } from '../grid/experimental.grid';
import { selectTemplate } from '../grid/reusable-grid.function';
import { FocusSelectorDaysToAchievement } from '../model/focus-selector-days-to-achievement.model';
import { FocusSelectorFocusModel } from '../model/focus-selector-focus.model';
import { FocusSelectorGoalModel } from '../model/focus-selector-goal.model';
import {
    ColumnID,
    LoadGoalsGrid,
} from '../model/focus-selector-load-goals.grid.model';
import { FocusSelectorMetricModel } from '../model/focus-selector-metric.model';
import { ProjectionEvaluationRangeId } from '../model/focus-selector-projection-evaluation-range.enum';
import { FocusSelectorTriggerSource } from '../model/focus-selector-trigger-source.enum';
import {
    SelectedGoalFormFields,
    SelectedGoalFormFieldsModel,
} from '../model/load-goals-form-fields.model';
import { LoadGoalsGoalChartModel } from '../model/load-goals-goal-chart.model';
import {
    LoadGoalsSelectedGoal,
    LoadGoalsSelectedGoals,
} from '../model/load-goals-selected.model';
import { FocusSelectorSelectGoalRow } from '../state/load-goals-select.action';
import { FocusSelectorUpdateSelectedGoal } from '../state/load-goals-update.action';

import {
    calcGoalValueFromPercImprovement,
    calcPercImprovementFromGoalValue,
    calcStartPercToGoal,
    dateIsValid,
    determineDaysToAchievement,
} from './load-goals.function';

@Component({
    selector: 'cmps-load-goals',
    templateUrl: './load-goals.component.html',
    host: { class: 'cmps-load-goals', '[class.row-mode]': '!_forceColumn' },
})
//
export class LoadGoalsComponent implements OnInit, OnChanges {
    @Input() performerId: number;
    @Input() goalThresholds: GoalThreshold[];
    @Input() activeMetric: FocusSelectorMetricModel;
    @Input() selectedFocus: FocusSelectorFocusModel;
    @Input() selectedGoals: LoadGoalsSelectedGoals;
    @Input() gridData: LoadGoalsGrid;
    @Input() triggerSource: FocusSelectorTriggerSource;

    @Output() selectRow: EventEmitter<FocusSelectorSelectGoalRow>;
    @Output() updateSelectedGoal: EventEmitter<FocusSelectorUpdateSelectedGoal>;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _selectTpl = selectTemplate;
    /** @private */ _ColumnID = ColumnID;
    /** @private */ _CoachingType = CoachingType;
    /** @private */ _ProjectionEvalId = ProjectionEvaluationRangeId;
    /** @private */ _PeriodType = PeriodType;
    /** @private */ _GraphType = GraphType;
    /** @private */ _FormFields = SelectedGoalFormFields;
    /** @private */ _forceColumn: boolean;
    /** @private */ _showGrid: boolean;
    /** @private */ _activeGoalMetricIds: { [metricId: number]: CommitmentKpi };
    /** @private */ _forms: { [metricId: number]: CmpsForm };
    /** @private */ _goalChart: { [metricId: number]: LoadGoalsGoalChartModel };
    /** @private */ _daysToAchievement: {
        [metricId: number]: FocusSelectorDaysToAchievement;
    };

    /** @private */ _minDate: NgbDateStruct;
    /** @private */ _selectedProjEvalColMap: Map<
        number,
        ProjectionEvaluationRangeId
    >;

    private readonly _Today = new Date();

    constructor(
        private readonly _dialogLayoutService: DialogLayoutService,
        private readonly _translateService: TranslateService,
        private readonly _formBuilder: CmpsFormBuilder,
        private readonly _ngbDateAdapter: NgbDateAdapter<Date>,
        private readonly _teamOAService: TeamOAService,
        private readonly _goalService: GoalService,
    ) {
        this._showGrid = false;
        this.selectRow = new EventEmitter();
        this.updateSelectedGoal = new EventEmitter();
        this._activeGoalMetricIds = {};
        this._forms = {};
        this._goalChart = {};
        this._daysToAchievement = {};
        this._minDate = this._ngbDateAdapter.fromModel(this._Today);
        this._selectedProjEvalColMap = new Map<
            number,
            ProjectionEvaluationRangeId
        >();
    }

    ngOnInit(): void {
        this._showGrid = this._shouldShowGrid();

        this._dialogLayoutService.forceColumn
            .pipe(takeUntilDestroy(this))
            .subscribe((forceColumn) => {
                this._forceColumn = forceColumn;
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.activeMetric != null || changes.selectedFocus != null) {
            this._showGrid = this._shouldShowGrid();
        }

        if (changes.selectedFocus != null) {
            this._forms = {};
            this._activeGoalMetricIds = {};
            this._daysToAchievement = {};
            this._goalChart = {};
        }

        if (changes.gridData != null) {
            this._removeDeselectedGoals();
            this._handleSelectedGoals();
        }
    }

    private _selectCell(
        selectedRowId: Row['id'],
        projectionEvaluationColumn: ProjectionEvaluationRangeId | null,
    ): void {
        const metricId = parseInt(selectedRowId);

        if (
            projectionEvaluationColumn === null &&
            !this._selectedProjEvalColMap.has(metricId)
        ) {
            this._selectedProjEvalColMap.set(
                metricId,
                this._ProjectionEvalId.DAY_30,
            );
            this.selectRow.emit({
                rowAndMetricId: metricId,
                projectionEvaluationColumn: this._ProjectionEvalId.DAY_30,
            });
        } else {
            if (projectionEvaluationColumn === null) {
                this._selectedProjEvalColMap.delete(metricId);
            } else {
                this._selectedProjEvalColMap.set(
                    metricId,
                    projectionEvaluationColumn,
                );
            }
            this.selectRow.emit({
                rowAndMetricId: metricId,
                projectionEvaluationColumn,
            });
        }
    }

    private _isProjectionCellSelected(
        selectedProjEvalCol: LoadGoalsGrid['selection']['projectionEvalCol'],
        rowAndMetricId: Row['id'],
        range: ProjectionEvaluationRangeId,
    ): boolean {
        const metricId = parseInt(rowAndMetricId, 10);
        if (this._selectedProjEvalColMap.get(metricId) != null) {
            return this._selectedProjEvalColMap.get(metricId) === range;
        }
        if (this.triggerSource === FocusSelectorTriggerSource.MANAGE_GOALS) {
            return false;
        }
        if (
            selectedProjEvalCol.metricId === metricId &&
            selectedProjEvalCol.range === range
        ) {
            this._selectedProjEvalColMap.set(metricId, range);
            return true;
        }
        return false;
    }

    /** @private */ _getCoachableTranslationText(
        coachTypeId: CoachableMetric['coachTypeId'],
    ): string {
        switch (coachTypeId) {
            case this._CoachingType.NON_OPPORTUNITY:
                return this._translateService.instant(
                    TranslationKeyMap._11090_METRIC_IS_ALREADY_BEING_COACHED_BUT_IS_NOT_AN_OPPORTUNITY_KPI,
                );
            case this._CoachingType.COACHED:
                return this._translateService.instant(
                    TranslationKeyMap._11089_METRIC_IS_ALREADY_BEING_COACHED,
                );
            case this._CoachingType.UNCOACHED:
                return this._translateService.instant(
                    TranslationKeyMap._11088_METRIC_IS_AN_CURRENT_OPPORTUNITY,
                );
            default:
                return '';
        }
    }

    private _shouldShowGrid(): boolean {
        return (
            this.activeMetric?.id != null &&
            this.selectedFocus?.behaviour?.id != null &&
            this.selectedFocus?.subBehaviour?.id != null
        );
    }

    private _removeDeselectedGoals(): void {
        for (const keyAndMetricId in this._forms) {
            const selectedGoal = this.selectedGoals[keyAndMetricId];

            if (selectedGoal == null) {
                this._forms[keyAndMetricId] = null;
                this._goalChart[keyAndMetricId] = null;
                this._daysToAchievement[keyAndMetricId] = null;
                this._activeGoalMetricIds[keyAndMetricId] = null;
            }
        }
    }

    private _handleSelectedGoals(): void {
        for (const keyAndMetricId in this.selectedGoals) {
            const metricId = parseInt(keyAndMetricId);

            const selectedGoal = this.selectedGoals[metricId];
            this._daysToAchievement[metricId] = determineDaysToAchievement(
                selectedGoal.form?.expectedAchievementDate,
                this.goalThresholds,
            );
            if (this._forms[metricId] == null) {
                if (selectedGoal.activeGoal != null) {
                    this._activeGoalMetricIds[metricId] =
                        selectedGoal.activeGoal;
                } else {
                    const matchingGoal = this.gridData.selection.goals.find(
                        (goal) => goal.metricId === metricId,
                    );

                    this._forms[metricId] = this._createNewForm(
                        selectedGoal,
                        matchingGoal,
                    );
                    this._goalChart[metricId] =
                        this._createNewGoalChart(matchingGoal);
                    this._retrieveGoalChartData(matchingGoal);
                }
            } else {
                this._updateFormWithGoalChanges(
                    selectedGoal,
                    this._forms[metricId].form,
                );
            }
        }
    }

    private _generateEmptyStateGoalChartData(): any[] {
        const r = [0, 1, 2, 3, 4, 5, 6];
        return r.map((a, idx) => {
            return {
                target: 0,
                actual: 3,
                eventDate: addDays(new Date(), a),
            };
        });
    }

    private _fillDraftGoalChartData(
        goal: FocusSelectorGoalModel,
        goalForm: FormGroup,
    ): any[] {
        if (goalForm == null) {
            return [];
        }

        const goalValue = parseFloat(
            goalForm.controls[this._FormFields.GOAL_VALUE.name].value,
        );
        const expectedAchievementDate =
            goalForm.controls[this._FormFields.EXPECTED_ACHIEVEMENT_DATE.name]
                .value;

        return [
            {
                goalValue: goal.actual,
                expectedAchievementDate: startOfDay(new Date()),
            },
            {
                goalValue,
                expectedAchievementDate,
            },
        ];
    }

    private _updateGoalChartGoalData(
        goal: FocusSelectorGoalModel,
        form: FormGroup,
    ): void {
        const goalChart = this._goalChart?.[goal.metricId];
        const goalChartData = goalChart?.data;
        const showEmptyGoalChart = goalChart?.showEmptyGraph;
        if (goalChartData != null && !showEmptyGoalChart) {
            this._goalChart[goal.metricId].data = [
                {
                    ...this._goalChart[goal.metricId].data[0],
                    goalData: this._fillDraftGoalChartData(goal, form),
                },
            ];
        }
    }

    private _createNewGoalChart(
        goal: FocusSelectorGoalModel,
    ): LoadGoalsGoalChartModel {
        return {
            showEmptyGraph: true,
            activeMetric: {
                metric: {
                    metricId: goal.metricId.toString(),
                    metricName: goal.metric,
                },
            },
            data: [
                {
                    mainData: [...this._generateEmptyStateGoalChartData()],
                    goalData: [],
                    targetData: [],
                    sgAvgData: [],
                    eventData: [],
                },
            ],
        };
    }

    private _retrieveGoalChartData(goal: FocusSelectorGoalModel): void {
        const opts = {
            periodType: PeriodType.ROLLING_30,
            date: new Date(),
            startDate: addDays(new Date(), -90),
            userId: this.performerId,
            metricId: [goal.metricId],
            displayNameCriteriaIdType: CriteriaIdType.USER_ID,
            outputCriteriaIdType: CriteriaIdType.USER_ID,
        };

        const sgTrendDataObs =
            this._teamOAService.retrieveCoachingPerformanceData(
                GraphType.TREND,
                {
                    viewType: PerformanceDataViewType.SUPER_GROUP,
                    ...opts,
                },
            );

        const userTrendDataObs =
            this._teamOAService.retrieveCoachingPerformanceData(
                GraphType.TREND,
                {
                    viewType: PerformanceDataViewType.USER_SUPER_GROUP,
                    ...opts,
                },
            );

        forkJoin([sgTrendDataObs, userTrendDataObs])
            .pipe(takeUntilDestroy(this))
            .subscribe((data) => {
                if (this._goalChart[goal.metricId] != null) {
                    if (data?.[1]?.length != null && data[1].length === 0) {
                        return;
                    }

                    this._goalChart[goal.metricId].data = [
                        {
                            ...this._goalChart[goal.metricId].data[0],
                            mainData: data[1] ?? [],
                            goalData: this._fillDraftGoalChartData(
                                goal,
                                this._forms[goal.metricId].form,
                            ),
                            sgAvgData: data[0] ?? [],
                        },
                    ];

                    this._goalChart[goal.metricId].showEmptyGraph = false;
                }
            });
    }

    private _setDaysToAchievement(metricId: number, value: Date): void {
        this._daysToAchievement[metricId] = determineDaysToAchievement(
            value,
            this.goalThresholds,
        );
    }

    private _createNewForm(
        selectedGoal: LoadGoalsSelectedGoal,
        matchingGoal: FocusSelectorGoalModel,
    ): CmpsForm<SelectedGoalFormFieldsModel> {
        const newCmpsForm =
            this._formBuilder.create<SelectedGoalFormFieldsModel>(
                SelectedGoalFormFields,
            );
        const newForm = newCmpsForm.form;
        this._subToExpecAchievDateValChanges(matchingGoal, newForm);
        this._subToPercImprovExpecValChanges(matchingGoal, newForm);
        this._subToGoalValChanges(matchingGoal, newForm);

        this._setInitialFormValues(selectedGoal, newForm);

        return newCmpsForm;
    }

    private _subToExpecAchievDateValChanges(
        goal: FocusSelectorGoalModel,
        form: FormGroup,
    ): void {
        form.controls[
            this._FormFields.EXPECTED_ACHIEVEMENT_DATE.name
        ].valueChanges
            .pipe(
                debounceTime(500),
                filter<Date>((date) => dateIsValid(date)),
                tap((date) => {
                    this._setDaysToAchievement(goal.metricId, date);
                    this._updateGoalChartGoalData(goal, form);

                    this._updateStateWithSelectedGoal(goal.metricId);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _subToPercImprovExpecValChanges(
        goal: FocusSelectorGoalModel,
        form: FormGroup,
    ): void {
        form.controls[
            this._FormFields.PERCENT_IMPROVEMENT_EXPECTED.name
        ].valueChanges
            .pipe(
                debounceTime(500),
                tap((val) => {
                    if (val == null) {
                        return form.controls[
                            this._FormFields.PERCENT_IMPROVEMENT_EXPECTED.name
                        ].setValue('0.00');
                    }

                    const percImprovement = parseFloat(val);

                    const goalValueFormCtrl =
                        form.controls[this._FormFields.GOAL_VALUE.name];
                    const startPercentToGoalFormCtrl =
                        form.controls[
                            this._FormFields.START_PERCENT_TO_GOAL.name
                        ];

                    const goalValue = calcGoalValueFromPercImprovement(
                        percImprovement / 100,
                        goal.actual,
                        goal.rankOrder,
                    );

                    goalValueFormCtrl.setValue(goalValue, {
                        emitEvent: false,
                    });

                    startPercentToGoalFormCtrl.setValue(
                        calcStartPercToGoal(
                            goalValue,
                            goal.actual,
                            goal.rankOrder,
                        ),
                        { emitEvent: false },
                    );

                    this._updateGoalChartGoalData(goal, form);
                    this._updateStateWithSelectedGoal(goal.metricId);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _subToGoalValChanges(
        goal: FocusSelectorGoalModel,
        form: FormGroup,
    ): void {
        form.controls[this._FormFields.GOAL_VALUE.name].valueChanges
            .pipe(
                debounceTime(500),
                tap((val) => {
                    if (val == null) {
                        return form.controls[
                            this._FormFields.GOAL_VALUE.name
                        ].setValue('0.00');
                    }

                    const goalValue = parseFloat(val);

                    const percentImprovExpecFormCtrl =
                        form.controls[
                            this._FormFields.PERCENT_IMPROVEMENT_EXPECTED.name
                        ];

                    const startPercentToGoalFormCtrl =
                        form.controls[
                            this._FormFields.START_PERCENT_TO_GOAL.name
                        ];

                    const percentImprovementExpected =
                        calcPercImprovementFromGoalValue(
                            goalValue,
                            goal.actual,
                            goal.rankOrder,
                        );

                    percentImprovExpecFormCtrl.setValue(
                        percentImprovementExpected,
                        { emitEvent: false },
                    );

                    startPercentToGoalFormCtrl.setValue(
                        calcStartPercToGoal(
                            goalValue,
                            goal.actual,
                            goal.rankOrder,
                        ),
                        { emitEvent: false },
                    );

                    this._updateGoalChartGoalData(goal, form);
                    this._updateStateWithSelectedGoal(goal.metricId);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _setInitialFormValues(
        selectedGoal: LoadGoalsSelectedGoal,
        form: FormGroup,
    ): void {
        form.patchValue(
            {
                [this._FormFields.CURRENT_ACTUAL.name]:
                    selectedGoal?.form?.currentActual,
                [this._FormFields.PERCENT_GAP_TO_TARGET.name]:
                    selectedGoal?.form?.percentGapToTarget,
                [this._FormFields.PERCENT_IMPROVEMENT_EXPECTED.name]:
                    selectedGoal?.form?.percentImprovementExpected,
                [this._FormFields.GOAL_VALUE.name]:
                    selectedGoal?.form?.goalValue,
                [this._FormFields.START_PERCENT_TO_GOAL.name]:
                    selectedGoal?.form?.startPercentToGoal,
            },
            { emitEvent: false },
        );

        form.controls[this._FormFields.EXPECTED_ACHIEVEMENT_DATE.name].setValue(
            selectedGoal?.form?.expectedAchievementDate,
        );
    }

    private _updateFormWithGoalChanges(
        selectedGoal: LoadGoalsSelectedGoal,
        form: FormGroup,
    ): void {
        form.patchValue({
            [this._FormFields.EXPECTED_ACHIEVEMENT_DATE.name]:
                selectedGoal.form?.expectedAchievementDate,
            [this._FormFields.PERCENT_IMPROVEMENT_EXPECTED.name]:
                selectedGoal.form?.percentImprovementExpected,
        });
    }

    private _updateStateWithSelectedGoal(metricId: number): void {
        this.updateSelectedGoal.emit({
            metricId,
            updates: {
                form: this._forms?.[metricId]?.form?.value ?? null,
                daysToAchievement:
                    this._daysToAchievement?.[metricId]?.daysToAchievement ??
                    null,
                activeGoal: this._activeGoalMetricIds?.[metricId] ?? null,
            },
        });
    }
}
